import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authApi } from '../api/services';
import BrandMark from '../components/BrandMark';
import AuthBackdrop from '../components/auth/AuthBackdrop';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { score: 25, label: 'Weak', color: '#EF5350' };
  if (score <= 3) return { score: 50, label: 'Fair', color: '#FFA726' };
  if (score <= 4) return { score: 75, label: 'Good', color: '#FFA726' };
  return { score: 100, label: 'Strong', color: '#66BB6A' };
}

// ---------------------------------------------------------------------------
// PasswordResetPage — completes the password reset flow started in
// ForgotPasswordPage. Reads the email + token from the URL query string,
// shows a strength meter while the user types a new password, and a
// matched/mismatched hint under the confirm field.
// ---------------------------------------------------------------------------

export default function PasswordResetPage() {
  const params = new URLSearchParams(window.location.search);
  const emailParam = params.get('email') || '';
  const tokenParam = params.get('token') || '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword({
        email: emailParam,
        token: tokenParam,
        newPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (Array.isArray(errors)) {
        setError(errors.join(' '));
      } else {
        setError(err.response?.data?.error || 'Password reset failed. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0E17]">
      <AuthBackdrop />
      <div
        className="relative z-10 mx-4 w-full max-w-[420px] rounded-2xl border border-white/10 p-10 shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_80px_rgba(124,58,237,0.05)]"
        style={{
          background: 'rgba(18, 24, 41, 0.85)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div className="mb-1">
          <BrandMark size="md" showStratiumLine accent="light" />
        </div>
        <p className="mb-8 text-[13px] tracking-wide text-white/60">Set your password</p>

        {error && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success ? (
          <>
            <Alert variant="success" className="mb-6">
              <AlertDescription>
                Password reset successfully. You can now sign in with your new password.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate('/login')}
              size="lg"
              className="h-12 w-full bg-gradient-to-br from-violet-600 to-violet-700 text-base font-semibold text-white shadow-[0_4px_14px_rgba(124,58,237,0.3)] hover:from-violet-700 hover:to-violet-800"
            >
              Go to Sign In
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {emailParam && (
              <p className="text-[13px] text-white/60">
                Setting password for <strong className="text-white/90">{emailParam}</strong>
              </p>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-password" className="text-white/80">New Password</Label>
              <Input
                id="new-password"
                type="password"
                required
                autoFocus
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-11 bg-white/[0.03] text-white"
              />
            </div>
            {newPassword && (
              <div className="flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${strength.score}%`, backgroundColor: strength.color }}
                  />
                </div>
                <p
                  className="min-w-[40px] text-[11px]"
                  style={{ color: strength.color }}
                >
                  {strength.label}
                </p>
              </div>
            )}
            <p className="text-[11px] text-white/40">
              Min 8 chars, uppercase, lowercase, number, special character
            </p>

            <div className="mt-1 flex flex-col gap-1.5">
              <Label htmlFor="confirm-password" className="text-white/80">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-11 bg-white/[0.03] text-white"
              />
              {confirmPassword && (
                <p
                  className={cn(
                    'text-[11px]',
                    passwordsMatch ? 'text-emerald-400' : 'text-rose-400',
                  )}
                >
                  {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="mt-2 h-12 gap-2 bg-gradient-to-br from-violet-600 to-violet-700 text-base font-semibold text-white shadow-[0_4px_14px_rgba(124,58,237,0.3)] hover:from-violet-700 hover:to-violet-800"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Reset Password'}
            </Button>
          </form>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="text-xs font-semibold text-violet-400 hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

